import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Mountain, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fireSubscriptionConversion } from "@/lib/analytics";

const UNLOCKED = [
    "Gestão de processos ANM, prazos e alertas automáticos",
    "Assistente de IA especializado em mineração",
    "Pesquisa de legislação com fontes oficiais",
    "Inteligência geoespacial e central de documentos",
];

export default function PaymentSuccess() {
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [state, setState] = useState("checking"); // checking | paid | failed
    const [attempts, setAttempts] = useState(0);
    const isAddon = new URLSearchParams(window.location.search).get("type") === "addon";

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get("session_id");
        if (!sessionId) { setState("failed"); return; }

        let cancelled = false;
        const poll = async (n) => {
            if (cancelled) return;
            try {
                const res = await api.get(`/payments/status/${sessionId}`);
                if (res.data.payment_status === "paid") {
                    await refresh();
                    setState("paid");
                    if (!isAddon) fireSubscriptionConversion(sessionId);
                    return;
                }
                if (res.data.status === "expired" || res.data.payment_status === "failed") {
                    setState("failed"); return;
                }
            } catch (e) { console.error("Payment status poll failed:", e); }
            if (n >= 8) { setState("failed"); return; }
            setAttempts(n + 1);
            setTimeout(() => poll(n + 1), 2000);
        };
        poll(0);
        return () => { cancelled = true; };
    }, [refresh]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 topo-texture p-6">
            <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden" data-testid="payment-result">
                {state === "checking" && (
                    <div className="p-10 text-center">
                        <Loader2 className="w-14 h-14 text-petroleum mx-auto animate-spin" />
                        <h1 className="font-heading text-2xl font-black text-slate-900 mt-5">{isAddon ? "Confirmando pagamento do pacote..." : "Confirmando sua assinatura..."}</h1>
                        <p className="text-slate-500 mt-2 text-sm">{isAddon ? "Aguarde, estamos validando seu pacote adicional de IA." : "Aguarde, estamos confirmando seu pagamento no Mercado Pago."} ({attempts}/8)</p>
                    </div>
                )}
                {state === "paid" && !isAddon && (
                    <div data-testid="payment-confirmed">
                        <div className="bg-darkblue text-white px-8 py-6 flex items-center gap-2 relative overflow-hidden">
                            <div className="absolute inset-0 opacity-10 topo-texture" />
                            <Mountain className="w-6 h-6 text-mineral relative" />
                            <span className="font-heading font-black text-lg relative">GEOMINE MANAGER <span className="text-mineral">GM² ANM</span></span>
                        </div>
                        <div className="p-10 text-center">
                            <div className="w-20 h-20 rounded-full bg-green-50 border-4 border-green-100 flex items-center justify-center mx-auto">
                                <CheckCircle2 className="w-11 h-11 text-green-500" />
                            </div>
                            <span className="inline-flex items-center gap-1.5 mt-6 text-[11px] font-bold uppercase tracking-wider text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1">
                                <Sparkles className="w-3.5 h-3.5" /> Assinatura confirmada
                            </span>
                            <h1 className="font-heading text-3xl font-black text-slate-900 mt-4">Bem-vindo ao Geomine Manager! 🎉</h1>
                            <p className="text-slate-500 mt-2 text-sm">Seu pagamento foi confirmado e sua assinatura está <b className="text-slate-700">ativa</b>. A cobrança é mensal e recorrente — você pode cancelar quando quiser.</p>

                            <ul className="mt-6 text-left space-y-2.5 bg-slate-50 border border-slate-100 rounded-xl p-5">
                                {UNLOCKED.map((b) => (
                                    <li key={b} className="flex gap-2.5 text-sm text-slate-700">
                                        <CheckCircle2 className="w-4 h-4 text-mineral shrink-0 mt-0.5" /> {b}
                                    </li>
                                ))}
                            </ul>

                            <button onClick={() => navigate("/dashboard")} className="mt-8 w-full bg-mineral hover:bg-mineralHover text-white rounded-sm py-3.5 font-bold shadow-lg transition-colors inline-flex items-center justify-center gap-2" data-testid="payment-go-dashboard">
                                Acessar minha conta <ArrowRight className="w-4 h-4" />
                            </button>
                            <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-400">
                                <ShieldCheck className="w-3.5 h-3.5" /> Pagamento processado com segurança pelo Mercado Pago
                            </p>
                        </div>
                    </div>
                )}
                {state === "paid" && isAddon && (
                    <div className="p-10 text-center">
                        <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
                        <h1 className="font-heading text-2xl font-black text-slate-900 mt-5">Pacote adicional ativado!</h1>
                        <p className="text-slate-500 mt-2 text-sm">Foram adicionados 250.000 tokens à sua franquia deste mês.</p>
                        <button onClick={() => navigate("/assistente")} className="mt-8 w-full bg-petroleum hover:bg-petroleumHover text-white rounded-sm py-3 font-semibold transition-colors" data-testid="payment-go-dashboard">
                            Voltar ao Assistente
                        </button>
                    </div>
                )}
                {state === "failed" && (
                    <div className="p-10 text-center">
                        <XCircle className="w-16 h-16 text-red-500 mx-auto" />
                        <h1 className="font-heading text-2xl font-black text-slate-900 mt-5">Pagamento não confirmado</h1>
                        <p className="text-slate-500 mt-2 text-sm">Se você concluiu o pagamento, aguarde alguns instantes — a confirmação do Mercado Pago pode levar um momento. Caso contrário, tente novamente.</p>
                        <button onClick={() => navigate(isAddon ? "/assistente" : "/assinatura")} className="mt-8 w-full bg-petroleum hover:bg-petroleumHover text-white rounded-sm py-3 font-semibold transition-colors" data-testid="payment-retry">
                            {isAddon ? "Voltar ao Assistente" : "Voltar para assinatura"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
